import trace from '~/lib/trace';
import {useEffect, useState, useCallback} from 'react';
import {View, ActivityIndicator} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';

import {
  service_request as serviceRequestApi,
  rob as robApi,
  fleet as fleetApi,
} from '~/api/private';
import Format from '~/lib/format';

import sy from '~/styles';
import {
  Text,
  RadioButton,
  Link,
  Pressable,
  Dialog,
  TireTooltip,
  ActionButton,
  Grouping,
} from '~/components/controls';
import Actionsheet, {Title, Actions} from '~/components/controls/Actionsheet';
import {AddTireModal} from '~/components/shared/Modals';
import {
  ROB_TIRE_SWAP_CODE,
  ROB_TIRE_REPLACE_CODE,
  ROB_TIRE_REPAIR_CODE,
  TIRE_STATE,
  TIRE_STATE_LABEL,
  TIRE_SOURCE,
  TIRE_SOURCE_LABEL,
  ROB_TIRE_CODES,
  LOCATION_TEXT,
  TIRE_REPAIR,
  TIRE_SWAP_SEASON_INDICATOR,
} from '~/types/rob';
import {useDispatch} from '~/lib/hooks';

import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import ArchiveIcon from '~/images/md-icons/archive/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import HandshakeIcon from '~/images/md-icons/handshake/materialicons/24px.svg';
import ReceiptLongIcon from '~/images/md-icons/receipt_long/materialicons/24px.svg';

import {SeasonIndicator, Profile, Linking} from '../shared';
import PropTypes from 'prop-types';

const SCREEN = {
  Task: 'Task',
  SLA: 'SLA',
  Reason: 'Reason',
  Source: 'Source',
  StoredReason: 'StoredReason',
};

const Tire_Sources = {
  [ROB_TIRE_REPLACE_CODE]: [
    TIRE_SOURCE.New,
    TIRE_SOURCE.Reserve,
    TIRE_SOURCE.Used,
    TIRE_SOURCE.LessorStock,
    TIRE_SOURCE.ThirdParty,
    TIRE_SOURCE.Private,
  ],
  [ROB_TIRE_SWAP_CODE]: [
    TIRE_SOURCE.Stored,
    TIRE_SOURCE.New,
    TIRE_SOURCE.Reserve,
    TIRE_SOURCE.Used,
    TIRE_SOURCE.LessorStock,
    TIRE_SOURCE.ThirdParty,
  ],
};

const UNMOUNT_ACTION = {
  None: 'None',
  Remove: 'Remove',
  Store: 'Store',
  Repair: 'Repair',
};

const Unmount_Actions = [
  {
    key: UNMOUNT_ACTION.None,
    label: 'Geen actie',
    icon: <ListIcon fill="4a4a49" />,
  },
  {
    key: UNMOUNT_ACTION.Remove,
    label: 'Afvoeren',
    icon: <DeleteIcon fill="4a4a49" />,
    sla_items: [
      {
        rob_code: ROB_TIRE_REPLACE_CODE,
        default_source: Tire_Sources[ROB_TIRE_REPLACE_CODE][0],
        default_tire_state: TIRE_STATE.Worn,
        label: 'Bandvervanging',
        action: 'Current_Replace',
      },
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel zonder velg',
        action: 'Current_Remove',
      },
    ],
  },
  {
    key: UNMOUNT_ACTION.Store,
    label: 'Opslaan',
    icon: <ArchiveIcon fill="4a4a49" />,
    sla_items: [
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel zonder velg',
        action: 'Current_Store',
      },
      {
        rob_code: ROB_TIRE_SWAP_CODE,
        default_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_source: Tire_Sources[ROB_TIRE_SWAP_CODE][0],
        default_stored_tire_state: TIRE_STATE.Worn,
        label: 'Wissel met velg',
        action: 'Current_StoreWheel',
        is_wheel: true,
      },
    ],
  },
  {
    key: UNMOUNT_ACTION.Repair,
    label: 'Bewerken',
    icon: <TireRepairIcon fill="4a4a49" />,
    sla_items: [
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Repareren',
        action: TIRE_REPAIR.Repareren,
        operation_code: '03',
      },
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Balanceren',
        action: TIRE_REPAIR.Balanceren,
        operation_code: '20',
      },
      {
        rob_code: ROB_TIRE_REPAIR_CODE,
        label: 'Omwisselen',
        action: TIRE_REPAIR.Wielomwisselen,
        operation_code: '28',
      },
    ],
  },
];

const resolveUnmountAction = (action) => {
  if (!action) {
    return null;
  }

  for (const item of Unmount_Actions) {
    const sla_item = item.sla_items?.find((entry) => entry.action === action);

    if (sla_item) {
      return {
        key: item.key,
        tireAction: sla_item,
      };
    }
  }

  return null;
};

const NEW_TIRE_INITIAL_PROFILE_WIDTH = 8;

export const getTiresEnablePrice = (sla, location, action) => {
  for (const key in sla?.tires_enable ?? {}) {
    const condition = key.split(',');

    if (
      condition[0] === location &&
      condition[1] === action.rob_code &&
      condition[2] === action.action
    ) {
      return sla.tires_enable[key];
    }
  }

  return null;
};

export const isSlaPriceEditable = ({rob_components, sla, location, action}) => {
  if (!rob_components || !sla?.tires_enable) {
    return false;
  }

  const is_swap_allowed = !!rob_components[ROB_TIRE_SWAP_CODE];
  const is_replace_allowed = !!rob_components[ROB_TIRE_REPLACE_CODE];

  if (!is_swap_allowed || !is_replace_allowed) {
    return !isNil(getTiresEnablePrice(sla, location, action));
  }

  return false;
};

const SlaPrice = ({
  rob_components,
  customPrice,
  action,
  sla,
  preview,
  location,
  style,
  onEdit,
}) => {
  let price = sla.codes.find(
    (item) =>
      item.rob_code === action.rob_code &&
      item.action === null &&
      item.condition === null,
  )?.price;

  const sla_item = sla.codes.find(
    (item) =>
      item.rob_code === action.rob_code &&
      item.action === action.action &&
      item.condition === null,
  );

  if (sla_item) {
    price = sla_item.price;
  }

  if (!price) {
    price = preview?.service ?? preview?.[action.action];
  }

  const tiresEnablePrice = getTiresEnablePrice(sla, location, action);
  const editable = isSlaPriceEditable({rob_components, sla, location, action});

  const displayPrice = customPrice ?? price;

  return editable ? (
    <Link
      textStyle={style}
      onPress={() => onEdit(action, price, tiresEnablePrice)}>
      {displayPrice > 0 ? Format.price(displayPrice) : ''}
    </Link>
  ) : (
    <Text style={style}>
      {displayPrice > 0 ? Format.price(displayPrice) : ''}
    </Text>
  );
};

SlaPrice.propTypes = {
  rob_components: PropTypes.object,
  customPrice: PropTypes.number,
  action: PropTypes.shape({
    rob_code: PropTypes.string,
    action: PropTypes.string,
  }),
  sla: PropTypes.shape({
    codes: PropTypes.array,
    tires_enable: PropTypes.object,
  }),
  preview: PropTypes.object,
  location: PropTypes.string,
  style: PropTypes.any,
  onEdit: PropTypes.func,
};

const PRICE_TYPES = {
  SLA: 'sla',
  TiresEnable: 'tires_enable',
};

const CostsPreviewDialog = ({
  visible,
  lessor,
  part_preview,
  newTire,
  sla,
  onDismiss,
}) => {
  let invoice_discount = sla?.invoice?.find(
    (item) => item.condition?.tire_make === newTire?.makeCode,
  );

  if (!invoice_discount) {
    invoice_discount = sla?.invoice?.find((item) => isNil(item.condition));
  }

  return (
    <Dialog
      visible={visible}
      title={<Text style={sy.largePlus}>Details</Text>}
      titleIcon={<ReceiptLongIcon fill="#4a4a49" />}
      options={{noPaddingContent: true}}
      onDismiss={onDismiss}>
      <View style={[sy['pt-4']]}>
        <View style={[sy['px-4'], sy['pb-4'], sy['gap-4']]}>
          {Object.keys(part_preview ?? {})
            .sort((a, b) => {
              if (a === 'tire_price') {
                return -1;
              }

              if (b === 'tire_price') {
                return 1;
              }

              const label_a = COST_LABEL[a] ?? a;
              const label_b = COST_LABEL[b] ?? b;

              return label_a.localeCompare(label_b);
            })
            .map((key) => (
              <View key={key} style={[sy['flex-row'], sy['justify-between']]}>
                <Text>
                  {key === 'tire_price' && newTire
                    ? newTire.title
                    : COST_LABEL[key] ?? key}
                </Text>
                <Text style={[sy.smallRegular]}>
                  {Format.price(part_preview[key])}
                </Text>
              </View>
            ))}
        </View>
        {newTire?.rob_tire_id && (
          <Grouping header="Band" initial={{expand: true}}>
            <View style={[sy['py-4'], sy['gap-4']]}>
              <View
                style={[
                  sy['px-4'],
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['gap-4'],
                ]}>
                <View style={[sy['flex-1']]}>
                  <Text>{newTire.title}</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    {newTire.description}
                  </Text>
                </View>
                <Text style={[sy.smallRegular]}>
                  {Format.price(newTire.price)}
                </Text>
              </View>
              {newTire.discount > 0 && (
                <>
                  <Divider style={sy['bg-gray95']} />
                  <View
                    style={[
                      sy['px-4'],
                      sy['flex-row'],
                      sy['justify-between'],
                      sy['gap-4'],
                    ]}>
                    <View>
                      <Text>Na korting in opdracht</Text>
                      <Text style={[sy.small, sy['text-lightgray']]}>
                        {Format.number(
                          (newTire.discount / newTire.price) * 100,
                        )}
                        % &nbsp;&middot;&nbsp;{lessor}
                      </Text>
                    </View>
                    <Text style={[sy.smallRegular]}>
                      {Format.price(newTire.price - newTire.discount)}
                    </Text>
                  </View>
                </>
              )}
              {invoice_discount?.discount > 0 && (
                <>
                  <Divider style={sy['bg-gray95']} />
                  <View
                    style={[
                      sy['px-4'],
                      sy['flex-row'],
                      sy['justify-between'],
                      sy['gap-4'],
                    ]}>
                    <View>
                      <Text>Na korting bij factuur</Text>
                      <Text style={[sy.small, sy['text-lightgray']]}>
                        {Format.number(invoice_discount.discount * 100)}
                        %&nbsp;&middot;&nbsp;{lessor}
                      </Text>
                    </View>
                    <Text style={[sy.smallRegular]}>
                      {Format.price(
                        newTire.price -
                          newTire.discount -
                          (newTire.price - newTire.discount) *
                            invoice_discount.discount,
                      )}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </Grouping>
        )}
      </View>
    </Dialog>
  );
};
CostsPreviewDialog.propTypes = {
  visible: PropTypes.any,
  lessor: PropTypes.any,
  part_preview: PropTypes.any,
  newTire: PropTypes.any,
  sla: PropTypes.any,
  onDismiss: PropTypes.any,
};

const EditPriceDialog = ({
  visible,
  initial_value,
  sla_price,
  action,
  tires_enable_price,
  onChange,
  onDismiss,
}) => {
  const [price, setPrice] = useState(initial_value);
  const [type, setType] = useState(PRICE_TYPES.SLA);

  useEffect(() => {
    setType(
      initial_value === tires_enable_price
        ? PRICE_TYPES.TiresEnable
        : PRICE_TYPES.SLA,
    );
  }, [initial_value, tires_enable_price]);

  return (
    <Dialog
      visible={visible}
      title="Prijs aanpassen"
      onDismiss={() => {
        onChange(initial_value);
        onDismiss();
      }}
      buttons={[
        {
          text: 'Annuleren',
          onPress: () => {
            onChange(initial_value);
            onDismiss();
          },
        },
        {
          text: 'OK',
          onPress: () => {
            onChange(price);
            onDismiss();
          },
        },
      ]}
      options={{noPaddingContent: true}}>
      <View>
        <View
          style={[sy['flex-row'], sy['items-center'], sy['gap-4'], sy['p-4']]}>
          <RadioButton
            checked={type === PRICE_TYPES.SLA}
            onPress={() => {
              setType(PRICE_TYPES.SLA);
              setPrice(sla_price);
            }}
          />
          <View style={[sy['flex-1']]}>
            <Text>SLA&nbsp;&middot;&nbsp;{action?.label}</Text>
          </View>
          <Text style={[sy.smallRegular, sy['self-start']]}>
            {Format.price(sla_price ?? 0)}
          </Text>
        </View>
        <Divider style={[sy['bg-gray95'], sy['ml-14']]} />
        <View
          style={[sy['flex-row'], sy['items-center'], sy['gap-4'], sy['p-4']]}>
          <RadioButton
            checked={type === PRICE_TYPES.TiresEnable}
            onPress={() => {
              setType(PRICE_TYPES.TiresEnable);
              setPrice(tires_enable_price);
            }}
          />
          <View style={[sy['flex-1']]}>
            <Text>Verzoek toestaan bandvervanging</Text>
          </View>
          <Text style={[sy.smallRegular, sy['self-start']]}>
            {Format.price(tires_enable_price)}
          </Text>
        </View>
      </View>
    </Dialog>
  );
};
EditPriceDialog.propTypes = {
  visible: PropTypes.any,
  initial_value: PropTypes.any,
  sla_price: PropTypes.any,
  action: PropTypes.any,
  tires_enable_price: PropTypes.any,
  onChange: PropTypes.any,
  onDismiss: PropTypes.any,
};

const TIRE_COST = {
  service: 'service',
  disposalFee: 'disposalFee',
  surchargeRunOnFlat: 'surchargeRunOnFlat',
  storageCosts: 'storageCosts',
  tire_price: 'tire_price',
  surchargeTireSource: 'surchargeTireSource',
  custom_price: 'custom_price',
};

const COST_LABEL = {
  [TIRE_COST.service]: 'Servicetarief',
  [TIRE_COST.disposalFee]: 'Verwijderingsbijdrage',
  [TIRE_COST.surchargeRunOnFlat]: 'ROF toeslag',
  [TIRE_COST.storageCosts]: 'Opslagkosten',
  [TIRE_COST.tire_price]: 'Bandprijs',
  [TIRE_COST.surchargeTireSource]: 'Toeslag uit voorraad LM',
  [TIRE_COST.custom_price]: 'Verzoek toestaan bandvervanging',
};

const MountedTire = ({
  mounted,
  location,
  linked,
  setMounted,
  setShowAddTireModal,
}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4']]}>
      <Profile
        location={location}
        widths={[location, ...(linked ?? [])].reduce((acc, key) => {
          acc[key] = mounted[key].profile_width;
          return acc;
        }, {})}
        onChange={(values) =>
          setMounted((prevState) => {
            const new_state = {...prevState};

            Object.keys(values).forEach((item) => {
              new_state[item].profile_width = values[item];
            });

            return new_state;
          })
        }
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>
              {mounted[location].rob_tire_id ? (
                mounted[location].title
              ) : (
                <>Onbekend</>
              )}
            </Text>
            <SeasonIndicator indicator={mounted[location].seasonIndicator} />
          </View>
          <Link
            textStyle={[sy.small, sy['text-lightgray'], sy.truncate]}
            onPress={() =>
              setShowAddTireModal({
                rob_tire_id: mounted[location].rob_tire_id,
                all_tires_allowed: true,
                onSelected: (selected_tire) =>
                  setMounted((prevState) => {
                    const new_state = {...prevState};

                    new_state[location] = {
                      ...new_state[location],
                      rob_tire_id: selected_tire.id,
                      title: `${selected_tire.make} ${
                        selected_tire.modelTypeProfile
                      } ${selected_tire.typeNumber ?? ''}`.trim(),
                      description: selected_tire.description,
                      category: selected_tire.category,
                      seasonIndicator: selected_tire.seasonIndicator,
                      rimDiameterInInches: selected_tire.rimDiameterInInches,
                    };

                    return new_state;
                  }),
              })
            }>
            {mounted[location].rob_tire_id ? (
              mounted[location].description
            ) : (
              <>Keuze maken</>
            )}
          </Link>
        </View>
      </View>
    </View>
  </View>
);
MountedTire.propTypes = {
  mounted: PropTypes.any,
  location: PropTypes.any,
  linked: PropTypes.any,
  setMounted: PropTypes.any,
  setShowAddTireModal: PropTypes.any,
};

const StoredTire = ({
  stored,
  location,
  linked,
  setStored,
  setShowAddTireModal,
}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4']]}>
      <Profile
        location={location}
        widths={[location, ...(linked ?? [])].reduce((acc, key) => {
          acc[key] = stored[key].profile_width;
          return acc;
        }, {})}
        onChange={(values) => {
          setStored((prevState) => {
            const newState = {...prevState};

            Object.keys(values).forEach((item) => {
              newState[item].profile_width = values[item];
            });

            return newState;
          });
        }}
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>
              {stored[location]?.rob_tire_id ? (
                stored[location].title
              ) : (
                <>Onbekend</>
              )}
            </Text>
            <SeasonIndicator indicator={stored[location]?.seasonIndicator} />
          </View>
          <Link
            textStyle={[sy.small, sy['text-lightgray'], sy.truncate]}
            onPress={() =>
              setShowAddTireModal({
                rob_tire_id: stored[location]?.rob_tire_id,
                all_tires_allowed: false,
                onSelected: (selected_tire) =>
                  setStored((prevState) => ({
                    ...prevState,
                    [location]: {
                      ...prevState[location],
                      rob_tire_id: selected_tire.id,
                      title: `${selected_tire.make} ${
                        selected_tire.modelTypeProfile
                      } ${selected_tire.typeNumber ?? ''}`.trim(),
                      description: selected_tire.description,
                      category: selected_tire.category,
                      seasonIndicator: selected_tire.seasonIndicator,
                      rimDiameterInInches: selected_tire.rimDiameterInInches,
                    },
                  })),
              })
            }>
            {stored[location]?.rob_tire_id ? (
              stored[location].description
            ) : (
              <>Keuze maken</>
            )}
          </Link>
        </View>
      </View>
    </View>
  </View>
);
StoredTire.propTypes = {
  stored: PropTypes.any,
  location: PropTypes.any,
  linked: PropTypes.any,
  setStored: PropTypes.any,
  setShowAddTireModal: PropTypes.any,
};

const NewTire = ({
  newTire,
  mounted,
  stored,
  location,
  tireAction,
  mountedTireSource,
  lessor,
  brands,
  setNewTire,
  setShowAddTireModal,
}) => (
  <View style={[sy['flex-row']]}>
    <View style={[sy['p-4'], sy['pr-4']]}>
      <Profile
        location={location}
        widths={{
          [location]: newTire.profile_width,
        }}
        onChange={(values) =>
          setNewTire((prevState) => ({
            ...prevState,
            profile_width: values[location],
          }))
        }
      />
    </View>
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['flex-1'],
          sy['justify-between'],
          sy['py-4'],
          sy['pr-4'],
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
            <Text style={sy.truncate}>
              {newTire.rob_tire_id ? newTire.title : <>Onbekend</>}
            </Text>
            <SeasonIndicator indicator={newTire.seasonIndicator} />
          </View>
          <Link
            textStyle={[sy.small, sy['text-lightgray'], sy.truncate]}
            onPress={() =>
              setShowAddTireModal({
                rob_tire_id:
                  newTire.rob_tire_id ??
                  (tireAction.rob_code === ROB_TIRE_SWAP_CODE
                    ? stored[location]?.rob_tire_id
                    : mounted[location]?.rob_tire_id),
                all_tires_allowed:
                  tireAction.rob_code === ROB_TIRE_REPLACE_CODE,
                initial_state: {
                  seasonIndicators:
                    tireAction.rob_code === ROB_TIRE_SWAP_CODE
                      ? [
                          ...TIRE_SWAP_SEASON_INDICATOR[
                            mounted[location]?.seasonIndicator
                          ],
                        ]
                      : [mounted[location]?.seasonIndicator],
                },
                onSelected: (selected_tire) =>
                  setNewTire((prevState) => ({
                    ...prevState,
                    rob_tire_id: selected_tire.id,
                    title: `${selected_tire.make} ${
                      selected_tire.modelTypeProfile
                    } ${selected_tire.typeNumber ?? ''}`.trim(),
                    description: selected_tire.description,
                    category: selected_tire.category,
                    makeCode: selected_tire.makeCode,
                    eanCode: selected_tire.eanCode,
                    rimDiameterInInches: selected_tire.rimDiameterInInches,
                    manufacturerSuggestedRetailPrice:
                      selected_tire.manufacturerSuggestedRetailPrice,
                    seasonIndicator: selected_tire.seasonIndicator,
                    profile_width:
                      prevState.profile_width ?? NEW_TIRE_INITIAL_PROFILE_WIDTH,
                    ...([
                      TIRE_SOURCE.Reserve,
                      TIRE_SOURCE.LessorStock,
                      TIRE_SOURCE.ThirdParty,
                      TIRE_SOURCE.Private,
                    ].includes(mountedTireSource)
                      ? {
                          price: 0,
                          discount: 0,
                        }
                      : {
                          price: selected_tire.manufacturerSuggestedRetailPrice,
                          discount: selected_tire.discount,
                        }),
                  })),
              })
            }>
            {newTire.rob_tire_id ? newTire.description : <>Keuze maken</>}
          </Link>
        </View>
        {newTire.rob_tire_id ? (
          <View style={[sy['pl-2']]}>
            <Text style={sy.smallRegular}>
              {newTire.price > 0
                ? Format.price(newTire.price - newTire.discount)
                : null}
            </Text>
            <View style={[sy['flex-row'], sy['justify-end']]}>
              {newTire.price > 0 ? (
                <TireTooltip
                  lessor={lessor}
                  {...newTire}
                  order={brands[newTire.makeCode]?.[0]?.order}
                  discount={Format.round(
                    1 -
                      newTire.discount /
                        newTire.manufacturerSuggestedRetailPrice,
                    2,
                  )}
                />
              ) : null}
            </View>
          </View>
        ) : null}
      </View>
    </View>
  </View>
);
NewTire.propTypes = {
  newTire: PropTypes.any,
  mounted: PropTypes.any,
  stored: PropTypes.any,
  location: PropTypes.any,
  tireAction: PropTypes.any,
  mountedTireSource: PropTypes.any,
  lessor: PropTypes.any,
  brands: PropTypes.any,
  setNewTire: PropTypes.any,
  setShowAddTireModal: PropTypes.any,
};

const ActionsActionsheet = (props) => {
  const dispatch = useDispatch();

  const {
    request_id,
    is_fleet_car,
    is_retail_car,
    license,
    selected,
    onDismiss,
    onChange,
  } = props;

  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState(SCREEN.Task);
  const [lessor, setLessor] = useState(null);
  const [sla, setSLA] = useState(null);
  const [brands, setBrands] = useState(null);
  const [robComponents, setRobComponents] = useState(null);
  const [unmountAction, setUnmountAction] = useState(UNMOUNT_ACTION.None);
  const [tireAction, setTireAction] = useState(null);
  const [tireState, setTireState] = useState(null);
  const [storedTireState, setStoredTireState] = useState(null);
  const [mountedTireSource, setMountedTireSource] = useState(null);
  const [mounted, setMounted] = useState({});
  const [stored, setStored] = useState({});
  const [newTire, setNewTire] = useState(null);
  const [showAddTireModal, setShowAddTireModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [linked, setLinked] = useState(null);
  const [fleetPermissions, setFleetPermissions] = useState(null);
  const [customPrice, setCustomPrice] = useState(
    selected[props.location]?.customPrice,
  );

  const [showCostsPreviewDialog, setShowCostsPreviewDialog] = useState(false);
  const [showEditPriceDialog, setShowEditPriceDialog] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.get(request_id, dispatch);
      const {result: fleet_contract} = await fleetApi.contract(
        result[0].fleet_contract_id,
      );

      const {permissions} = fleet_contract.contract;
      setFleetPermissions({
        [ROB_TIRE_REPAIR_CODE]: permissions?.tires?.repair,
        [ROB_TIRE_REPLACE_CODE]: permissions?.tires?.replace,
        [ROB_TIRE_SWAP_CODE]: permissions?.tires?.swap,
      });
    };

    if (is_fleet_car) {
      fetch().catch(trace);
    }
  }, [request_id, is_fleet_car, dispatch]);

  const updateTireAction = (action) => {
    setTireAction((prevState) => {
      if (prevState !== action) {
        setTireState(action?.default_tire_state);
        setStoredTireState(action?.default_stored_tire_state);
        if (
          action?.default_stored_source &&
          stored[props.location]?.rob_tire_id
        ) {
          updateMountedTireSource(action.default_stored_source);
        } else {
          updateMountedTireSource(action?.default_source);
        }
      }

      return action;
    });
  };

  const updateMountedTireSource = (source) => {
    setMountedTireSource((prevState) => {
      if (prevState !== source) {
        setNewTire(!source || source === TIRE_SOURCE.Stored ? null : {});
      }

      return source;
    });
  };

  useEffect(() => {
    const location = props.location;

    const unmount_action =
      resolveUnmountAction(selected[location].repairs?.[0]) ??
      resolveUnmountAction(selected[location].action);

    if (unmount_action) {
      setUnmountAction(unmount_action.key);
      setTireAction(unmount_action.tireAction);
    }

    setTireState(
      selected[location].attributes?.tire_state ??
        unmount_action?.tireAction?.default_tire_state,
    );

    setStoredTireState(
      selected[location].stored.attributes?.tire_state ?? TIRE_STATE.Worn,
    );

    setMountedTireSource(
      (selected[location].new_tire
        ? selected[location].new_tire.source ?? TIRE_SOURCE.New
        : null) ??
        (unmount_action?.tireAction?.default_stored_source &&
        selected[location]?.stored?.attributes?.rob_tire_id
          ? unmount_action?.tireAction?.default_stored_source
          : unmount_action?.tireAction?.default_source),
    );

    setMounted(
      Object.keys(selected).reduce((acc, item) => {
        acc[item] = {
          ...selected[item]?.attributes,
        };
        return acc;
      }, {}),
    );

    setStored(
      Object.keys(selected).reduce((acc, item) => {
        acc[item] = {
          ...selected[item]?.stored?.attributes,
        };
        return acc;
      }, {}),
    );

    setNewTire(selected[location]?.new_tire);

    const fetch = async () => {
      const {result: sla} = (await serviceRequestApi.sla(request_id)) ?? {};
      setLessor(sla?.lessor);
      setSLA({
        codes: sla?.tires?.codes ?? [],
        tires_enable: sla?.tires?.tires_enable ?? {},
        invoice: sla?.tires?.invoice ?? null,
      });

      const brands_sla =
        sla?.tires?.brands?.reduce((acc, item) => {
          acc[item.makeCode] = [
            ...(acc[item.makeCode] ?? []),
            {
              discount: item.discount,
              condition: item.condition,
              order: item.order,
            },
          ];
          return acc;
        }, {}) ?? {};
      setBrands(brands_sla ?? {});

      if (!is_fleet_car) {
        const {result: rob_components} = await robApi.components(
          license,
          request_id,
          ROB_TIRE_CODES,
        );

        setRobComponents(rob_components);
      }

      setLoading(false);
    };

    fetch().catch(trace);
  }, [
    license,
    request_id,
    props.location,
    fleetPermissions,
    selected,
    is_fleet_car,
  ]);

  const processChanges = useCallback(() => {
    const locations = [props.location, ...(linked ?? [])];
    const changes = locations.reduce((acc, location) => {
      const stored_tire = {
        // This is intentional so first take the attributes of the stored tire according to the tire of the currently selected location.
        // Then overwrite these attributes with the location specific stored tire attributes.
        ...stored[props.location],
        ...stored[location],
        tire_state:
          tireAction?.rob_code === ROB_TIRE_SWAP_CODE ? storedTireState : null,
      };

      let stored_action = null;
      if (tireAction?.rob_code === ROB_TIRE_SWAP_CODE) {
        if (mountedTireSource === TIRE_SOURCE.Stored) {
          stored_action = tireAction.is_wheel
            ? 'Stored_WheelChange'
            : 'Stored_TireChange';
        } else {
          stored_action = stored_tire.rob_tire_id ? 'Stored_ReplaceNew' : null;
        }
      }

      acc[location] = {
        customPrice:
          isFinite(customPrice) && location === props.location
            ? customPrice
            : null,
        rob_code: tireAction?.rob_code,
        action:
          tireAction?.rob_code === ROB_TIRE_REPAIR_CODE
            ? 'Current_TireRepair'
            : tireAction?.action,
        repairs:
          tireAction?.rob_code === ROB_TIRE_REPAIR_CODE
            ? [tireAction.action]
            : null,
        attributes: {
          ...(mounted[location]?.rob_tire_id == null
            ? mounted[props.location]
            : mounted[location]),
          tire_state: tireState,
          profile_width:
            mounted[location]?.profile_width ??
            mounted[props.location]?.profile_width,
        },
        stored: {
          action: stored_action,
          attributes: {
            ...stored_tire,
          },
        },
        new_tire:
          mountedTireSource && mountedTireSource !== TIRE_SOURCE.Stored
            ? {
                ...newTire,
                source: mountedTireSource,
              }
            : null,
      };

      return acc;
    }, {});

    return {
      ...changes,
    };
  }, [
    linked,
    tireAction,
    mounted,
    newTire,
    mountedTireSource,
    storedTireState,
    tireState,
    stored,
    props.location,
    customPrice,
  ]);

  const onOK = async () => {
    const changes = processChanges();
    onChange(changes);
  };

  useEffect(() => {
    setPreview(null);
    if (tireAction?.rob_code) {
      const fetch = async () => {
        const changes = processChanges();
        const {success, result} = await serviceRequestApi.preview_tires(
          request_id,
          {
            [props.location]: changes[props.location],
          },
        );

        if (success) {
          setPreview(result);
        }
      };

      fetch().catch(trace);
    }
  }, [request_id, unmountAction, tireAction, props.location, processChanges]);

  if (screen === SCREEN.Task) {
    const amount = preview?.reduce((acc, item) => acc + item.amount, 0);
    const part_preview = preview?.reduce((acc, item) => {
      if (item.parts?.[props.location]) {
        for (const cost in item.parts[props.location]) {
          if (!acc[cost]) {
            acc[cost] = 0;
          }

          acc[cost] += item.parts[props.location][cost];
        }
      }

      return acc;
    }, {});

    let part_costs = 0;
    if (part_preview) {
      part_costs =
        Object.values(part_preview).reduce((acc, amount) => acc + amount, 0) -
        part_preview.service -
        (part_preview.tire_price ?? 0);
    }

    return (
      <>
        <Actionsheet visible={true} dismissable={false} onDismiss={onDismiss}>
          {loading && (
            <View style={[sy['p-4']]}>
              <ActivityIndicator size="large" color="#231fda" />
            </View>
          )}
          {!loading && (
            <>
              <Title>
                <View
                  style={[
                    sy['flex-1'],
                    sy['flex-row'],
                    sy['items-between'],
                    sy['justify-between'],
                  ]}>
                  <Text style={[sy.large, {fontSize: 20}]}>
                    Band&nbsp;
                    {(
                      LOCATION_TEXT[props.location] ?? props.location
                    ).toLowerCase()}
                  </Text>
                  {props.location.startsWith('X') ? null : (
                    <Linking
                      value={linked}
                      location={props.location}
                      options={Object.keys(selected)}
                      onChange={setLinked}
                    />
                  )}
                </View>
              </Title>
              <Divider style={sy['bg-gray88']} />
              <MountedTire
                mounted={mounted}
                location={props.location}
                linked={linked}
                setMounted={setMounted}
                setShowAddTireModal={setShowAddTireModal}
              />
              <Divider style={sy['bg-gray88']} />
              {mounted[props.location].rob_tire_id && (
                <>
                  {Unmount_Actions.map((action, index, array) => {
                    const is_not_allowed =
                      action.sla_items?.every((item) => {
                        if (is_retail_car) {
                          return false;
                        }

                        if (fleetPermissions) {
                          return !fleetPermissions[item.rob_code];
                        }

                        return !robComponents?.[item.rob_code];
                      }) ?? false;

                    return (
                      <View key={action.key} style={[sy['flex-row']]}>
                        <Pressable
                          style={[sy['p-4']]}
                          disabled={is_not_allowed}
                          onPress={() => {
                            if (unmountAction !== action.key) {
                              setUnmountAction(action.key);
                              const sla_item = action.sla_items?.find(
                                (item) =>
                                  is_retail_car ||
                                  (fleetPermissions
                                    ? fleetPermissions[item.rob_code]
                                    : robComponents?.[item.rob_code]),
                              );
                              updateTireAction(sla_item);
                              updateMountedTireSource(sla_item?.default_source);
                              setCustomPrice(null);
                            }
                          }}>
                          <RadioButton
                            disabled={is_not_allowed}
                            checked={action.key === unmountAction}
                          />
                        </Pressable>
                        <View style={[sy['flex-1']]}>
                          <View
                            style={[
                              sy['flex-1'],
                              sy['flex-row'],
                              sy['py-4'],
                              sy['pr-4'],
                              sy['justify-between'],
                              is_not_allowed && {opacity: 0.5},
                            ]}>
                            <View>
                              <Text>{action.label}</Text>
                              {action.key === unmountAction && tireAction && (
                                <Pressable
                                  onPress={() => setScreen(SCREEN.SLA)}>
                                  <Text
                                    style={[
                                      sy.small,
                                      sy['text-lightgray'],
                                      sy.underline,
                                    ]}>
                                    SLA&nbsp;&middot;&nbsp;
                                    {tireAction.label}
                                  </Text>
                                </Pressable>
                              )}
                            </View>
                            {action.key === unmountAction && tireAction ? (
                              (() => {
                                if (part_preview?.service) {
                                  return (
                                    <Text style={[sy.smallRegular]}>
                                      {Format.price(part_preview.service)}
                                    </Text>
                                  );
                                }

                                return (
                                  <SlaPrice
                                    style={[sy.smallRegular]}
                                    rob_components={robComponents}
                                    customPrice={customPrice}
                                    action={tireAction}
                                    sla={sla}
                                    preview={part_preview}
                                    location={props.location}
                                    onEdit={(
                                      action,
                                      sla_price,
                                      tires_enable_price,
                                    ) => {
                                      setShowEditPriceDialog({
                                        action,
                                        sla_price,
                                        tires_enable_price,
                                      });
                                    }}
                                  />
                                );
                              })()
                            ) : (
                              <>{action.icon}</>
                            )}
                          </View>
                          {index < array.length - 1 && (
                            <Divider style={sy['bg-gray95']} />
                          )}
                        </View>
                      </View>
                    );
                  })}
                  <Divider style={sy['bg-gray88']} />
                </>
              )}
              {tireAction?.rob_code && (
                <>
                  {tireAction.rob_code === ROB_TIRE_REPLACE_CODE && (
                    <>
                      <View
                        style={[
                          sy['p-4'],
                          sy['flex-row'],
                          sy['justify-between'],
                          sy['items-center'],
                        ]}>
                        <Text style={[sy.mediumBold]}>Reden</Text>
                        <Link onPress={() => setScreen(SCREEN.Reason)}>
                          {TIRE_STATE_LABEL[tireState]}
                        </Link>
                      </View>
                      <Divider style={sy['bg-gray88']} />
                    </>
                  )}
                  {mountedTireSource && (
                    <>
                      <View
                        style={[
                          sy['p-4'],
                          sy['flex-row'],
                          sy['justify-between'],
                          sy['items-center'],
                        ]}>
                        <Text style={[sy.mediumBold]}>Artikel</Text>
                        <Link onPress={() => setScreen(SCREEN.Source)}>
                          {TIRE_SOURCE_LABEL[mountedTireSource]}
                        </Link>
                      </View>
                      {mountedTireSource === TIRE_SOURCE.Stored ? (
                        <>
                          <Divider style={sy['bg-gray95']} />
                          <StoredTire
                            stored={stored}
                            location={props.location}
                            linked={linked}
                            setStored={setStored}
                            setShowAddTireModal={setShowAddTireModal}
                          />
                        </>
                      ) : (
                        <>
                          {newTire && (
                            <>
                              <Divider style={sy['bg-gray95']} />
                              <NewTire
                                newTire={newTire}
                                mounted={mounted}
                                stored={stored}
                                location={props.location}
                                tireAction={tireAction}
                                mountedTireSource={mountedTireSource}
                                lessor={lessor}
                                brands={brands}
                                setNewTire={setNewTire}
                                setShowAddTireModal={setShowAddTireModal}
                              />
                            </>
                          )}
                        </>
                      )}
                      <Divider style={sy['bg-gray88']} />
                    </>
                  )}
                  {stored[props.location]?.rob_tire_id &&
                    mountedTireSource !== TIRE_SOURCE.Stored &&
                    tireAction.rob_code === ROB_TIRE_SWAP_CODE && (
                      <>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                            sy['items-center'],
                          ]}>
                          <Text style={[sy.mediumBold]}>Opslag afvoeren</Text>
                          <Link onPress={() => setScreen(SCREEN.StoredReason)}>
                            {TIRE_STATE_LABEL[storedTireState]}
                          </Link>
                        </View>
                        <>
                          <Divider style={sy['bg-gray95']} />
                          <StoredTire
                            stored={stored}
                            location={props.location}
                            linked={linked}
                            setStored={setStored}
                            setShowAddTireModal={setShowAddTireModal}
                          />
                        </>
                        <Divider style={sy['bg-gray88']} />
                      </>
                    )}
                  {part_preview && part_costs > 0 && (
                    <>
                      <View
                        style={[
                          sy['p-4'],
                          sy['flex-row'],
                          sy['justify-between'],
                          sy['items-center'],
                        ]}>
                        <Text style={[sy.mediumBold]}>Overige kosten</Text>
                        <Text style={[sy.smallRegular]}>
                          {Format.price(part_costs)}
                        </Text>
                      </View>
                      <Divider style={sy['bg-gray88']} />
                    </>
                  )}
                  {!is_fleet_car && !is_retail_car && tireAction && (
                    <>
                      <View
                        style={[
                          sy['p-4'],
                          sy['flex-row'],
                          sy['justify-between'],
                          sy['items-center'],
                        ]}>
                        <Text style={[sy.mediumBold]}>ROB activiteit</Text>
                        <Text>
                          {tireAction.rob_code}&nbsp;&middot;&nbsp;
                          {robComponents[tireAction.rob_code]?.description}
                        </Text>
                      </View>
                      <Divider style={sy['bg-gray88']} />
                    </>
                  )}
                </>
              )}
              <Actions style={[sy['justify-between']]}>
                <View style={[sy['flex-row'], sy['items-center']]}>
                  <Text style={[sy.mediumBold, sy['px-2']]}>
                    {amount ? Format.price(amount) : null}
                  </Text>
                  {amount > 0 && part_preview && (
                    <Pressable onPress={() => setShowCostsPreviewDialog(true)}>
                      <ReceiptLongIcon fill="#4a4a49" />
                    </Pressable>
                  )}
                </View>
                <View style={[sy['flex-row'], sy['gap-2']]}>
                  <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
                  <ActionButton onPress={onOK}>OK</ActionButton>
                </View>
              </Actions>
            </>
          )}
        </Actionsheet>

        <CostsPreviewDialog
          visible={showCostsPreviewDialog}
          lessor={lessor}
          part_preview={part_preview}
          newTire={newTire}
          sla={sla}
          onDismiss={() => setShowCostsPreviewDialog(false)}
        />

        <EditPriceDialog
          visible={!!showEditPriceDialog}
          initial_value={
            isNil(customPrice) ? showEditPriceDialog?.sla_price : customPrice
          }
          action={showEditPriceDialog?.action}
          sla_price={showEditPriceDialog?.sla_price}
          tires_enable_price={showEditPriceDialog?.tires_enable_price}
          onDismiss={() => setShowEditPriceDialog(null)}
          onChange={(price) => {
            if (showEditPriceDialog.sla_price === price) {
              setCustomPrice(null);
            } else {
              setCustomPrice(price);
            }
            setShowEditPriceDialog(null);
          }}
        />

        {showAddTireModal && (
          <AddTireModal
            license={license}
            request_id={request_id}
            rob_tire_id={showAddTireModal.rob_tire_id}
            initial_state={showAddTireModal.initial_state}
            onDismiss={() => setShowAddTireModal(false)}
            onChange={showAddTireModal.onSelected}
            all_tires_allowed={showAddTireModal.all_tires_allowed}
          />
        )}
      </>
    );
  }

  if (screen === SCREEN.SLA) {
    const action = Unmount_Actions.find(
      (action) => action.key === unmountAction,
    );

    const collect_allowed_repair_codes = (rob_component) => {
      return rob_component.operations[0].subcomponents[0].subcomponents
        .filter((item) => item.operation)
        .map((item) => item.operation.code);
    };

    return (
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title style={[{display: 'flex'}, sy['items-center']]}>
          <View style={[{paddingRight: 30}]}>
            <HandshakeIcon fill="#4a4a49" />
          </View>
          <Text style={[sy.large, {fontSize: 20, lineHeight: 30}]}>
            SLA items &middot; {action.label}
          </Text>
        </Title>
        <Divider style={sy['bg-gray88']} />
        {action.sla_items.map((item, index, array) => {
          let is_allowed = true;

          if (!is_retail_car) {
            if (fleetPermissions) {
              is_allowed = fleetPermissions[item.rob_code];
            } else {
              is_allowed = robComponents[item.rob_code];
            }
          }

          if (is_allowed && item.rob_code === ROB_TIRE_REPAIR_CODE) {
            is_allowed = is_fleet_car
              ? true
              : collect_allowed_repair_codes(
                  robComponents[item.rob_code],
                ).includes(item.operation_code);
          }

          if (!is_allowed) {
            return null;
          }

          return (
            <Pressable
              key={item.action}
              style={[sy['flex-row']]}
              onPress={() => updateTireAction(item)}>
              <View style={[sy['p-4']]}>
                <RadioButton checked={item.action === tireAction.action} />
              </View>
              <View style={[sy['flex-1'], sy['ml-4']]}>
                <View
                  style={[
                    sy['flex-1'],
                    sy['flex-row'],
                    sy['py-4'],
                    sy['pr-4'],
                    sy['justify-between'],
                    sy['items-center'],
                  ]}>
                  <Text>{item.label}</Text>
                  <SlaPrice style={[sy.smallRegular]} action={item} sla={sla} />
                </View>
                {index < array.length - 1 && (
                  <Divider style={sy['bg-gray95']} />
                )}
              </View>
            </Pressable>
          );
        })}
        <Divider style={sy['bg-gray88']} />
        <Actions>
          <ActionButton onPress={() => setScreen(SCREEN.Task)}>OK</ActionButton>
        </Actions>
      </Actionsheet>
    );
  }

  if (screen === SCREEN.Reason) {
    return (
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title style={[{display: 'flex'}, sy['items-center']]}>
          Reden afvoeren
        </Title>
        <Divider style={sy['bg-gray88']} />
        {[
          TIRE_STATE.Worn,
          TIRE_STATE.Punctured,
          TIRE_STATE.Loud,
          TIRE_STATE.Torn,
          TIRE_STATE.DriedOut,
        ].map((item, index, array) => (
          <Pressable
            key={item}
            style={[sy['flex-row']]}
            onPress={() => setTireState(item)}>
            <View style={[sy['p-4']]}>
              <RadioButton checked={tireState === item} />
            </View>
            <View style={[sy['flex-1'], sy['ml-4']]}>
              <View
                style={[sy['flex-1'], sy['flex-row'], sy['py-4'], sy['pr-4']]}>
                <Text>{TIRE_STATE_LABEL[item]}</Text>
              </View>
              {index < array.length - 1 && <Divider style={sy['bg-gray95']} />}
            </View>
          </Pressable>
        ))}
        <Divider style={sy['bg-gray88']} />
        <Actions>
          <ActionButton onPress={() => setScreen(SCREEN.Task)}>OK</ActionButton>
        </Actions>
      </Actionsheet>
    );
  }

  if (screen === SCREEN.Source) {
    return (
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title style={[{display: 'flex'}, sy['items-center']]}>
          Herkomst artikel
        </Title>
        <Divider style={sy['bg-gray88']} />
        {Tire_Sources[tireAction?.rob_code].map((item, index, array) => (
          <Pressable
            key={item}
            style={[sy['flex-row']]}
            onPress={() => updateMountedTireSource(item)}>
            <View style={[sy['p-4']]}>
              <RadioButton checked={mountedTireSource === item} />
            </View>
            <View style={[sy['flex-1'], sy['ml-4']]}>
              <View
                style={[sy['flex-1'], sy['flex-row'], sy['py-4'], sy['pr-4']]}>
                <Text>{TIRE_SOURCE_LABEL[item]}</Text>
              </View>
              {index < array.length - 1 && <Divider style={sy['bg-gray95']} />}
            </View>
          </Pressable>
        ))}
        <Divider style={sy['bg-gray88']} />
        <Actions>
          <ActionButton onPress={() => setScreen(SCREEN.Task)}>OK</ActionButton>
        </Actions>
      </Actionsheet>
    );
  }

  if (screen === SCREEN.StoredReason) {
    return (
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title style={[{display: 'flex'}, sy['items-center']]}>
          Reden afvoeren
        </Title>
        <Divider style={sy['bg-gray88']} />
        {[
          TIRE_STATE.Worn,
          TIRE_STATE.Punctured,
          TIRE_STATE.Loud,
          TIRE_STATE.Torn,
          TIRE_STATE.DriedOut,
        ].map((item, index, array) => (
          <Pressable
            key={item}
            style={[sy['flex-row']]}
            onPress={() => setStoredTireState(item)}>
            <View style={[sy['p-4']]}>
              <RadioButton checked={storedTireState === item} />
            </View>
            <View style={[sy['flex-1'], sy['ml-4']]}>
              <View
                style={[sy['flex-1'], sy['flex-row'], sy['py-4'], sy['pr-4']]}>
                <Text>{TIRE_STATE_LABEL[item]}</Text>
              </View>
              {index < array.length - 1 && <Divider style={sy['bg-gray95']} />}
            </View>
          </Pressable>
        ))}
        <Divider style={sy['bg-gray88']} />
        <Actions>
          <ActionButton onPress={() => setScreen(SCREEN.Task)}>OK</ActionButton>
        </Actions>
      </Actionsheet>
    );
  }
};
ActionsActionsheet.propTypes = {
  request_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  is_fleet_car: PropTypes.bool,
  is_retail_car: PropTypes.bool,
  license: PropTypes.string,
  selected: PropTypes.object,
  action: PropTypes.object,
  location: PropTypes.any,
  onDismiss: PropTypes.func,
  onChange: PropTypes.func,
};

export default ActionsActionsheet;
